import type { ApplogValue, EntityID } from '../applog/datom-types.ts'
import type { Thread } from '../thread/basic.ts'
import type { ObjectBuilder } from './builder.ts'

// ═══════════════════════════════════════════════════════════════
// Attribute Definition
// ═══════════════════════════════════════════════════════════════

export interface VMAttributeDef {
	/** Attribute name (short: e.g. 'name', 'content') */
	name: string
	/** The wovin at-path used in applogs (e.g. 'block/content', 'speaker/name') */
	atPath: string
	/** Whether the attribute is optional */
	optional: boolean
	/** Default value if not present in applogs */
	defaultValue?: ApplogValue
}

// ═══════════════════════════════════════════════════════════════
// Schema Adapter Interface
// ═══════════════════════════════════════════════════════════════

/**
 * A schema adapter converts a type-system schema (TypeBox, Zod, ArkType, Typia)
 * into a uniform representation that the VM factory can consume.
 */
export interface ISchemaAdapter<T extends Record<string, ApplogValue> = Record<string, ApplogValue>> {
	/** Get the attribute definitions for this schema */
	getAttributeDefs(): VMAttributeDef[]

	/** Get default values for all attributes */
	getDefaults(): Partial<T>

	/** Optional: runtime validator */
	createValidator?(): (value: unknown) => value is T

	/** The entity prefix used for wovin at-paths (e.g. 'block', 'speaker') */
	getEntityPrefix(): string
}

// ═══════════════════════════════════════════════════════════════
// ViewModel Factory Options
// ═══════════════════════════════════════════════════════════════

export interface ViewModelFactoryOptions<T extends Record<string, ApplogValue>> {
	/** The schema adapter for this entity type */
	adapter: ISchemaAdapter<T>

	/** How to get the default thread context */
	getDefaultThread?: () => Thread

	/** EntityID generation length (default: 7) */
	entityIdLength?: number

	/** Function to generate entity IDs */
	generateEntityId?: (data: Partial<T> & { ts?: string }) => EntityID

	/** Override the entity prefix for at-paths */
	entityPrefix?: string

	/** Optional function to make thread current-state (lastWriteWins) */
	makeCurrentThread?: (thread: Thread) => Thread

	/** Name for the VM type (used in logging and debugging) */
	vmName?: string

	/**
	 * Signal adapter for framework-specific reactivity.
	 * Default: non-reactive (direct Subscribable reads).
	 * For SolidJS, pass createSolidSignalAdapter() from @wovin/ui-solid/viewmodel.
	 */
	signalAdapter?: ISignalAdapter
}

// ═══════════════════════════════════════════════════════════════
// ViewModel Instance Interface
// ═══════════════════════════════════════════════════════════════

export interface IVMInstance<T extends Record<string, ApplogValue> = Record<string, ApplogValue>> {
	readonly en: EntityID
	readonly thread: Thread

	/** Get a builder for updating this entity */
	buildUpdate(init?: Partial<T>): ObjectBuilder<T>

	/** Soft-delete this entity */
	setDeleted(thread?: Thread): void

	/** Whether this entity is deleted */
	readonly isDeleted: boolean

	/** The entity thread filtered to this entity */
	readonly entityThread: Thread
}

// ═══════════════════════════════════════════════════════════════
// ViewModel Class Interface (what the factory returns)
// ═══════════════════════════════════════════════════════════════

export interface IVMClass<T extends Record<string, ApplogValue> = Record<string, ApplogValue>> {
	new(en: EntityID, thread: Thread, ...args: any[]): IVMInstance<T>

	/** Get or create a VM instance for the given entity ID and thread */
	get(en: EntityID, thread: Thread): IVMInstance<T>

	/** Create a new entity builder */
	buildNew(init?: Partial<T>, en?: EntityID): ObjectBuilder<T>
}

// ═══════════════════════════════════════════════════════════════
// Signal Adapter Interface (framework-specific reactivity)
// ═══════════════════════════════════════════════════════════════

/**
 * A signal adapter abstracts how the VM creates reactive signals for each attribute.
 * The core factory uses a default (non-reactive) implementation.
 * Framework-specific extensions (Solid, Vue, etc.) override this.
 */
export interface ISignalAdapter {
	/**
	 * Create a reactive getter for a subscribable value.
	 * Returns a function that returns the current value.
	 * In the default implementation, this is just () => subscribable.value.
	 */
	createGetter<T>(subscribable: { value: T; subscribe(cb: () => void): () => void }): () => T

	/**
	 * Create a writable signal for an attribute.
	 * Returns [get, set] pair.
	 * Default implementation stores and returns value directly.
	 */
	createWritable<T>(initial: T): [() => T, (v: T) => void]
}

// ═══════════════════════════════════════════════════════════════
// Shared state maps
// ═══════════════════════════════════════════════════════════════

export type VMInstanceMap<T> = Map<EntityID, T>

/** WeakMap from Thread → Map<VMName, Map<EntityID, VMInstance>> */
export const GlobalVMInstances = new WeakMap<Thread, Map<string, Map<EntityID, unknown>>>()

export function getInstancesForThread(thread: Thread): Map<string, Map<EntityID, unknown>> {
	let threadMap = GlobalVMInstances.get(thread)
	if (!threadMap) {
		threadMap = new Map()
		GlobalVMInstances.set(thread, threadMap)
	}
	return threadMap
}
